/**
 * Export Service
 * Handles data export requests that return downloadable files (XLSX / CSV).
 *
 * Unlike leadingAnalyticsAPI.exportData (which expects a JSON response), this
 * service treats the response as a binary blob and triggers a browser download
 * to the user's Downloads folder. It pairs with the backend fileGenerator
 * utility + the direct-download API Gateway response (isBase64Encoded).
 */

// Base Leading @ Gallo API URL.
// Prefers the REACT_APP_API_URL env var (appending the /leading path segment),
// and falls back to the DEV API Gateway endpoint when the env var is not set.
// DEV endpoint is used as the fallback intentionally so local/test builds do
// not hit production. Production builds should set REACT_APP_API_URL.
const LEADING_API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/leading`
  : 'https://ybtyyty4e9.execute-api.us-west-2.amazonaws.com/prod/leading';

// Path of the export endpoint (relative to LEADING_API_URL)
const EXPORT_ENDPOINT = '/api/analytics/export';

/**
 * Parse the filename from a Content-Disposition header.
 * Falls back to null if the header is absent or unparseable.
 * @param {string|null} disposition
 * @returns {string|null}
 */
function parseFilename(disposition) {
  if (!disposition) {
    return null;
  }
  // Handles: attachment; filename="cohort-1-analytics.xlsx"
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Trigger a browser download for a blob.
 * Creates a temporary object URL, clicks a hidden anchor, then cleans up.
 * @param {Blob} blob
 * @param {string} filename
 */
function triggerBrowserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Derive a sensible default filename when the server doesn't provide one.
 * @param {string} exportType
 * @param {string} format - 'CSV' | 'EXCEL'
 * @returns {string}
 */
function defaultFilename(exportType, format) {
  const base = (exportType || 'export').toLowerCase();
  const ext = (format || '').toUpperCase() === 'CSV' ? 'csv' : 'xlsx';
  const stamp = new Date().toISOString().slice(0, 10);
  return `${base}-${stamp}.${ext}`;
}

/**
 * Request an export from the backend and download the resulting file.
 *
 * @param {Function} getAccessToken - Async function returning an MSAL access token
 * @param {Object} options
 * @param {string} options.exportType - INDIVIDUAL_PROGRESS | COHORT_DATA | OGSM_SUMMARY
 * @param {string} options.format - 'CSV' | 'EXCEL'
 * @param {number} [options.cohortId] - Required for COHORT_DATA and OGSM_SUMMARY
 * @param {number} [options.participantId] - Required for INDIVIDUAL_PROGRESS
 * @param {Object} [options.extra] - Any additional fields to include in the request body
 * @returns {Promise<{filename: string}>} Resolves with the downloaded filename
 */
export async function downloadExport(getAccessToken, options = {}) {
  const {
    exportType,
    format,
    cohortId = null,
    participantId = null,
    extra = {}
  } = options;

  if (!exportType) {
    throw new Error('exportType is required');
  }
  if (!format) {
    throw new Error('format is required');
  }

  const token = await getAccessToken();

  const requestBody = {
    export_type: exportType,
    format,
    ...(cohortId !== null && { cohort_id: cohortId }),
    ...(participantId !== null && { participant_id: participantId }),
    ...extra
  };

  // Send an Accept header that matches the API Gateway binary media type for the
  // requested format. API Gateway decodes the base64 body to binary only when the
  // request's Accept header matches a configured binary media type, so we must
  // request the specific type (text/csv or the xlsx MIME) rather than */*.
  const acceptType = (format || '').toUpperCase() === 'CSV'
    ? 'text/csv'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  const response = await fetch(`${LEADING_API_URL}${EXPORT_ENDPOINT}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': acceptType
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    // The error body is JSON even when a successful response would be binary
    let message = `Export failed: HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      message = errorData.message || errorData.error || message;
    } catch (e) {
      // Response wasn't JSON; keep the default message
    }
    throw new Error(message);
  }

  // Read the file as a blob and download it
  const blob = await response.blob();
  const filename =
    parseFilename(response.headers.get('Content-Disposition')) ||
    defaultFilename(exportType, format);

  triggerBrowserDownload(blob, filename);

  return { filename };
}

/**
 * Convenience wrapper: export an individual participant's progress.
 * @param {Function} getAccessToken
 * @param {number} participantId
 * @param {string} format - 'CSV' | 'EXCEL'
 */
export function exportIndividualProgress(getAccessToken, participantId, format) {
  return downloadExport(getAccessToken, {
    exportType: 'INDIVIDUAL_PROGRESS',
    participantId,
    format
  });
}

/**
 * Convenience wrapper: export a cohort's analytics data.
 * @param {Function} getAccessToken
 * @param {number} cohortId
 * @param {string} format - 'CSV' | 'EXCEL'
 */
export function exportCohortData(getAccessToken, cohortId, format) {
  return downloadExport(getAccessToken, {
    exportType: 'COHORT_DATA',
    cohortId,
    format
  });
}

/**
 * Convenience wrapper: export a cohort's OGSM summary.
 * @param {Function} getAccessToken
 * @param {number} cohortId
 * @param {string} format - 'CSV' | 'EXCEL'
 */
export function exportOGSMSummary(getAccessToken, cohortId, format) {
  return downloadExport(getAccessToken, {
    exportType: 'OGSM_SUMMARY',
    cohortId,
    format
  });
}

/**
 * Convenience wrapper: export a multi-cohort comparison.
 * @param {Function} getAccessToken
 * @param {Array<number>|string} cohortIds - array or comma-separated string of cohort IDs
 * @param {string} format - 'CSV' | 'EXCEL'
 */
export function exportCohortComparison(getAccessToken, cohortIds, format) {
  // Normalize to a comma-separated string for the request body.
  const ids = Array.isArray(cohortIds) ? cohortIds.join(',') : cohortIds;
  return downloadExport(getAccessToken, {
    exportType: 'COHORT_COMPARISON',
    format,
    extra: { cohort_ids: ids }
  });
}

export default {
  downloadExport,
  exportIndividualProgress,
  exportCohortData,
  exportOGSMSummary,
  exportCohortComparison
};
